import { useMobileDetect } from "@/hooks";
import { useGetAllActiveGames } from "@/services/game.service";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import {
  Autoplay,
  Pagination,
  Navigation,
  Scrollbar,
  A11y,
} from "swiper/modules";
import "swiper/css/pagination";
import "swiper/css/navigation";
import {
  CarouselArrow,
  renderCarouselIcons,
} from "@/components/customCarousel";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { convertImageUrl } from "@/utils/games";

const CardGame = ({
  imageUrl,
  alt,
  url,
}: {
  imageUrl: string;
  alt: string;
  url: string;
}) => (
  <Link to={url}>
    <img className="w-[285px]" src={imageUrl} alt={alt} />
  </Link>
);

const headers: any = {
  slots: {
    logo: "/assets/sidebar/slots.svg",
    param: "slots",
    name: "Slots",
  },
  side: {
    logo: "/assets/sidebar/crash.svg",
    param: "battles",
    name: "Crash Games",
  },
  live: {
    logo: "/assets/sidebar/live.svg",
    param: "liveCasino",
    name: "Live Casino",
  },
  mini: {
    logo: "/assets/sidebar/table.svg",
    param: "sidebets",
    name: "Table Games",
  },
  instant: {
    logo: "/assets/sidebar/slots.svg",
    param: "instant",
    name: "Instant Games",
  },
};

const BestGames = () => {
  const swiperRef = useRef(null);
  const isMobile = useMobileDetect();

  const [filteredGames, setFilteredGames] = useState<any>(null);
  const { data, isSuccess } = useGetAllActiveGames();

  useEffect(() => {
    const newFilteredGames: any = {
      slots: [],
      mini: [],
      live: [],
      side: [],
      instant: [],
    };

    data?.data.forEach((g: any) => {
      switch (g.category) {
        case "SLOTS":
          newFilteredGames.slots.push(g);
          break;
        case "MINI_GAMES":
          newFilteredGames.mini.push(g);
          break;
        case "LIVE_CASINO":
          newFilteredGames.live.push(g);
          break;
        case "SIDE_BETS":
          newFilteredGames.side.push(g);
          break;
        case "INSTANT":
          newFilteredGames.instant.push(g);
          break;
        default:
      }
    });

    setFilteredGames(newFilteredGames);
  }, [isSuccess]);

  return (
    <div className="flex flex-col gap-2">
      {filteredGames !== null &&
        Object.keys(filteredGames).map((category: any, index: number) => {
          return (
            filteredGames[category].length > 0 && (
              <div key={category} className="w-full">
                <div className="mb-2 flex items-center justify-between">
                  <div className="font-base flex select-text gap-2 text-base text-sm text-white">
                    <img src={headers[category].logo} alt="slots-icon" />
                    {headers[category].name}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-primary-text">
                    <Link to={`/casino/lobby?tab=${headers[category].param}`}>
                      <p>View All</p>
                    </Link>
                    <CarouselArrow disabled={false} id={`prev-${category}`} />
                    <CarouselArrow
                      disabled={false}
                      direction="right"
                      id={`next-${category}`}
                    />
                  </div>
                </div>
                <Swiper
                  ref={swiperRef}
                  slidesPerView={isMobile ? 3 : 6}
                  slidesPerGroup={3}
                  loopAddBlankSlides={false}
                  className="max-h-[100px] min-h-[100px] lg:max-h-[132px] lg:min-h-[132px]"
                  navigation={{
                    enabled: true,
                    nextEl: `#next-${category}`,
                    prevEl: `#prev-${category}`,
                  }}
                  centeredSlides={false}
                  modules={[Autoplay, Navigation, Pagination]}
                  freeMode={true}
                  loop={true}
                  autoHeight
                  pagination={{
                    enabled: isMobile,
                    clickable: true,
                    type: "custom",
                    renderCustom: renderCarouselIcons,
                  }}
                  autoplay={{
                    delay: 5000 + index * 200,
                    disableOnInteraction: false,
                    reverseDirection: index % 2 == 0,
                  }}
                  speed={1300}
                  spaceBetween={16}
                >
                  {filteredGames[category]?.slice(0, 10).map((game: any) => {
                    return (
                      <SwiperSlide key={game.id}>
                        <Link to={`/casino/games/${game.slug}`}>
                          <LazyLoadImage
                            src={convertImageUrl(game.gameThumbnail)}
                            alt={game.name}
                          />
                        </Link>
                      </SwiperSlide>
                    );
                  })}
                </Swiper>
              </div>
            )
          );
        })}
    </div>
  );
};

const OriginalGames = () => {
  return (
    <div className="flex flex-col gap-4">
      <h4 className="mb-2 select-text font-kanit text-base font-semibold text-white md:text-xl">
        Sharkroll Originals
      </h4>
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
        <CardGame
          imageUrl="/assets/games/orig-spin-wheel.png"
          alt="spin-wheel"
          url="/games/spin-wheel"
        />
        <CardGame
          imageUrl="/assets/games/orig-bay-catch.png"
          alt="spin-wheel"
          url="/games/bay-catch"
        />
        <CardGame
          imageUrl="/assets/games/orig-shark-tower.png"
          alt="spin-wheel"
          url="/games/shark-tower"
        />
        <CardGame
          imageUrl="/assets/games/orig-predators-pick.png"
          alt="spin-wheel"
          url="/games/predators-pick"
        />
        <CardGame
          imageUrl="/assets/games/orig-shark-drift.png"
          alt="spin-wheel"
          url="/games/shark-drift"
        />
        <CardGame
          imageUrl="/assets/games/orig-marine-mystique.png"
          alt="spin-wheel"
          url="/games/marine-mystique"
        />
      </div>
    </div>
  );
};

export default BestGames;
